import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CircularProgress } from '@mui/material';
import { useTranslation } from '../hooks/useTranslation';
import { useOwnedProductIds } from '../hooks/useOwnedProductIds';
import { saveRedirectUrl } from '../utils/redirectHelpers';
import config from '../config';

interface Plugin {
  id: string;
  name: string;
  slug: string;
  description: string;
  detailedDescription?: string;
  price: number;
  version: string;
  imageUrl?: string;
  descriptionImageUrl?: string;
  gallery?: string[];
  audioSamples?: Array<{
    name: string;
    description: string;
    url: string;
    duration: number;
  }>;
  videoUrl?: string;
  features?: string[];
  systemRequirements?: {
    windows: string;
    mac: string;
    plugins: string[];
    ram: string;
    storage: string;
  };
  specifications?: {
    format: string[];
    bitDepth: string;
    sampleRate: string;
    latency: string;
    interface: string;
  };
  compatibility: string[];
  tags: string[];
  downloadFiles?: {
    macOS?: {
      filename: string;
      size: string;
      url: string;
    };
    windows?: {
      filename: string;
      size: string;
      url: string;
    };
  };
}

interface PluginResponse {
  success: boolean;
  data: Plugin;
  message?: string;
}

// Audio Player Component
const AudioPlayer: React.FC<{
  samples: Plugin['audioSamples'];
  currentAudio: HTMLAudioElement | null;
  playingTrack: string | null;
  isPaused: boolean;
  pluginSlug: string;
  onPlay: (audioUrl: string, trackId: string) => void;
}> = ({ samples, currentAudio, playingTrack, isPaused, pluginSlug, onPlay }) => {
  const { t } = useTranslation();
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (currentAudio) {
      const updateTime = () => setCurrentTime(currentAudio.currentTime);
      const updateDuration = () => {
        if (!isNaN(currentAudio.duration) && isFinite(currentAudio.duration)) {
          setDuration(currentAudio.duration);
        }
      };
      
      // Initialize duration immediately if already available
      if (!isNaN(currentAudio.duration) && isFinite(currentAudio.duration)) {
        setDuration(currentAudio.duration);
      }
      
      currentAudio.addEventListener('timeupdate', updateTime);
      currentAudio.addEventListener('loadedmetadata', updateDuration);
      currentAudio.addEventListener('durationchange', updateDuration);
      currentAudio.volume = 0.8; // Set fixed volume to 80%
      
      return () => {
        currentAudio.removeEventListener('timeupdate', updateTime);
        currentAudio.removeEventListener('loadedmetadata', updateDuration);
        currentAudio.removeEventListener('durationchange', updateDuration);
      };
    } else {
      // Reset duration when currentAudio is null
      setDuration(0);
      setCurrentTime(0);
    }
  }, [currentAudio]);

  const formatTime = (time: number) => {
    if (isNaN(time) || !isFinite(time)) {
      return '0:00';
    }
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (currentAudio && duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      currentAudio.currentTime = percent * duration;
    }
  };

  if (!samples || samples.length === 0) {
    return (
      <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-6 rounded-xl border border-gray-700/50">
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-purple-500/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
          </div>
          <p className="text-gray-400 text-sm">{t('plugin_detail.no_audio_samples')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Track List */}
      <div className="space-y-3 mb-6">
        {samples.map((sample, index) => (
          <div
            key={index}
            className={`flex items-center p-3 rounded-lg cursor-pointer transition-all duration-200 ${
              playingTrack === `sample-${index}`
                ? 'bg-purple-600/20 border border-purple-500/30'
                : 'hover:bg-gray-700/20 border border-transparent'
            }`}
            onClick={() => {
              // Use backend streaming proxy URL to avoid CORS issues
              if (!sample.url) {
                alert('Audio file not available');
                return;
              }
              
              // Use audio sample index instead of filename for proxy
              const proxyUrl = `${config.api.baseUrl}/api/plugins/${pluginSlug}/audio/${index}`;
              
              onPlay(proxyUrl, `sample-${index}`);
            }}
          >
            {/* Play Button */}
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 transition-colors duration-200 ${
              playingTrack === `sample-${index}`
                ? 'bg-purple-500 text-white'
                : 'bg-gray-600 text-gray-300 hover:bg-purple-500 hover:text-white'
            }`}>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                {playingTrack === `sample-${index}` && !isPaused ? (
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                ) : (
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                )}
              </svg>
            </div>

            {/* Track Info */}
            <div className="flex-1">
              <div className="font-medium text-white text-sm">{sample.name}</div>
              <div className="text-gray-400 text-xs">{sample.description}</div>
            </div>

            {/* Duration */}
            <div className="text-gray-400 text-xs">
              {playingTrack === `sample-${index}` && duration > 0 
                ? formatTime(duration)
                : sample.duration > 0
                  ? formatTime(sample.duration)
                  : '0:05' // Default expected duration for samples
              }
            </div>
          </div>
        ))}
      </div>

      {/* Player Controls */}
      {playingTrack && (
        <div className="pt-4">
          {/* Progress Bar */}
          <div className="mb-3">
            <div
              className="w-full h-2 bg-gray-700 rounded-full cursor-pointer"
              onClick={handleSeek}
            >
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-100"
                style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function PluginDetail() {
  const { t, currentLanguage } = useTranslation();
  const ownedIds = useOwnedProductIds();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [plugin, setPlugin] = useState<Plugin | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [playingTrack, setPlayingTrack] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (slug) {
      loadPlugin(slug);
    }
  }, [slug, currentLanguage]);

  const loadPlugin = async (pluginSlug: string) => {
    try {
      setLoading(true);
      const response = await axios.get<PluginResponse>(`${config.api.baseUrl}/api/plugins/${pluginSlug}?lang=${currentLanguage}`);
      
      if (response.data.success) {
        setPlugin(response.data.data);
      } else {
        setError(response.data.message || t('plugin_detail.not_found'));
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError(t('plugin_detail.not_found'));
      } else {
        setError(t('plugin_detail.failed_to_load'));
      }
    } finally {
      setLoading(false);
    }
  };

  const owned = plugin ? ownedIds.has(String(plugin.id)) : false;

  const handleBuyNow = () => {
    if (!plugin || owned) return;
    const destination = `/checkout/${plugin.slug}`;
    if (!localStorage.getItem('token')) {
      saveRedirectUrl(destination);
      navigate('/register');
      return;
    }
    navigate(destination);
  };

  const buyLabel = owned
    ? t('plugins.owned')
    : plugin?.price === 0
      ? t('plugin_detail.free_download')
      : t('plugin_detail.buy_now');
  const buyClass = owned
    ? 'cursor-not-allowed bg-gray-700 text-gray-400'
    : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg hover:shadow-xl';

  const playAudio = async (audioUrl: string, trackId: string) => {
    let durationChecker: NodeJS.Timeout | undefined;
    
    try {
      // If clicking on the same track
      if (playingTrack === trackId && currentAudio) {
        if (isPaused) {
          // Resume playback
          await currentAudio.play();
          setIsPaused(false);
        } else {
          // Pause playback
          currentAudio.pause();
          setIsPaused(true);
        }
        return;
      }
      
      // Stop current audio if playing different track
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }

      // Create new audio element
      const audio = new Audio();
      
      // Set preload for better compatibility - no crossOrigin needed for proxy
      audio.preload = 'metadata';
      
      // Set up event listeners before setting src
      audio.addEventListener('ended', () => {
        if (durationChecker) clearInterval(durationChecker);
        setPlayingTrack(null);
        setCurrentAudio(null);
        setIsPaused(false);
      });

      audio.addEventListener('error', (e) => {
        if (durationChecker) clearInterval(durationChecker);
        setPlayingTrack(null);
        setCurrentAudio(null);
        setIsPaused(false);
      });

      // Primary metadata event listener
      audio.addEventListener('loadedmetadata', () => {
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          if (durationChecker) clearInterval(durationChecker);
          // Only set currentAudio after we have valid duration
          setCurrentAudio(audio);
        }
      });

      // Additional event listeners for duration
      audio.addEventListener('canplay', () => {
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          if (durationChecker) clearInterval(durationChecker);
          setCurrentAudio(audio);
        }
      });

      audio.addEventListener('canplaythrough', () => {
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          if (durationChecker) clearInterval(durationChecker);
          setCurrentAudio(audio);
        }
      });

      audio.addEventListener('durationchange', () => {
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          if (durationChecker) clearInterval(durationChecker);
        setCurrentAudio(audio);
        }
      });

      // Set source and load
      audio.src = audioUrl;
      audio.load();

      // Wait a bit for metadata to potentially load before playing
      await new Promise(resolve => setTimeout(resolve, 500));

      // Set playing track immediately
      setPlayingTrack(trackId);
      setIsPaused(false);

      // Attempt to play
      await audio.play();

      // Don't set currentAudio here - wait for duration to be available first

      // Fallback: Check duration periodically for up to 10 seconds
      let attempts = 0;
      const maxAttempts = 100; // 10 seconds with 100ms intervals
      durationChecker = setInterval(() => {
        attempts++;
        
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          clearInterval(durationChecker);
          // Set currentAudio now that we have duration
          setCurrentAudio(audio);
          // Trigger durationchange event to update UI
          const durationEvent = new Event('durationchange');
          audio.dispatchEvent(durationEvent);
        } else if (attempts >= maxAttempts) {
          clearInterval(durationChecker);
        }
      }, 100);
      
    } catch (err: any) {
      // Clear interval if exists
      if (durationChecker) clearInterval(durationChecker);
      
      // Reset state on error
      setPlayingTrack(null);
      setCurrentAudio(null);
      setIsPaused(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-lg">{t('common.loading')}</div>
      </div>
    );
  }

  if (error || !plugin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">{error}</p>
          <button
            onClick={() => navigate('/plugins')}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-md transition-colors duration-200"
          >
            {t('plugin_detail.back_to_plugins')}
          </button>
        </div>
      </div>
    );
  }

  return (
          <div className="min-h-screen py-8">
              <div className="container mx-auto max-w-6xl px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/plugins')}
            className="text-purple-400 hover:text-purple-300 mb-4 flex items-center space-x-2 transition-colors duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>{t('plugin_detail.back_to_plugins')}</span>
          </button>
          
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Plugin Image */}
            <div className="lg:w-1/2">
              <div className="relative">
                <img
                  src={plugin.imageUrl}
                  alt={plugin.name}
                  className="w-full rounded-lg shadow-2xl"
                />
                <div className="absolute top-2 right-2">
                  <span className="bg-purple-500/90 text-white text-sm px-3 py-1 rounded-md font-medium">
                    v{plugin.version}
                  </span>
                </div>
              </div>
            </div>

            {/* Plugin Info */}
            <div className="lg:w-1/2">
              <h1 className="text-4xl font-bold text-white mb-4">{plugin.name}</h1>
              <p className="text-xl text-gray-300 mb-6">{plugin.description}</p>
              
              {/* Price and Buy Button */}
              <div className="mb-6">
                <div className="flex items-center space-x-4 mb-4">
                  {plugin.price === 0 ? (
                    <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-lg text-lg font-bold">
                      FREE
                    </span>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-white">${plugin.price}</span>
                      <span className="text-gray-400">{t('plugin_detail.one_time_purchase')}</span>
                    </>
                  )}
                </div>
                
                <button
                  type="button"
                  onClick={handleBuyNow}
                  disabled={owned}
                  className={`w-full lg:w-auto px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 ${buyClass}`}
                >
                  {buyLabel}
                </button>
              </div>

              {/* Quick Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-400">{t('plugin_detail.compatibility')}</div>
                  <div className="text-white">{plugin.compatibility.join(' • ')}</div>
                </div>
                <div>
                  <div className="text-gray-400">{t('plugin_detail.version')}</div>
                  <div className="text-white">{plugin.version}</div>
                </div>
                {plugin.downloadFiles?.macOS && (
                  <div>
                    <div className="text-gray-400">{t('plugin_detail.macos_size')}</div>
                    <div className="text-white">{plugin.downloadFiles.macOS.size}</div>
                  </div>
                )}
                {plugin.downloadFiles?.windows && (
                  <div>
                    <div className="text-gray-400">{t('plugin_detail.windows_size')}</div>
                    <div className="text-white">{plugin.downloadFiles.windows.size}</div>
                  </div>
                )}
              </div>

              {/* Tags */}
              {plugin.tags.length > 0 && (
                <div className="mt-6">
                  <div className="flex flex-wrap gap-2">
                    {plugin.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="text-xs border border-gray-600 text-gray-400 px-2 py-1 rounded-md"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Overview Section */}
        <div className="mb-12">
          <div className="mb-6">
            <div className="border-b border-gray-700">
              <h2 className="text-purple-400 font-medium text-sm pb-4 border-b-2 border-purple-500 inline-block">
                {t('plugin_detail.overview')}
              </h2>
            </div>
          </div>

          <div className="space-y-16">
            {/* Detailed Description */}
            {plugin.detailedDescription && (
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">{t('plugin_detail.description')}</h3>
                <div className="flex flex-col lg:flex-row gap-8">
                  {/* Text on the left */}
                  <div className="flex-1">
                    <div className="text-gray-300 whitespace-pre-line">{plugin.detailedDescription}</div>
                  </div>
                  {/* Image on the right */}
                  {plugin.descriptionImageUrl && (
                    <div className="lg:w-auto">
                      <img
                        src={plugin.descriptionImageUrl}
                        alt={`${plugin.name} description`}
                        className="rounded-lg shadow-lg"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/new_logo.png';
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* System Requirements */}
            {plugin.systemRequirements && (
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">{t('plugin_detail.system_requirements')}</h3>
                <div className="bg-gray-900/70 rounded-lg p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2">macOS</h4>
                      <p className="text-gray-300 text-sm">{t(plugin.systemRequirements.mac)}</p>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2">Windows</h4>
                      <p className="text-gray-300 text-sm">{t(plugin.systemRequirements.windows)}</p>
                    </div>
                    {plugin.systemRequirements.ram && (
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-2">RAM</h4>
                        <p className="text-gray-300 text-sm">{t(plugin.systemRequirements.ram)}</p>
                      </div>
                    )}
                    {plugin.systemRequirements.storage && (
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-2">Storage</h4>
                        <p className="text-gray-300 text-sm">{t(plugin.systemRequirements.storage)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Features */}
            {plugin.features && plugin.features.length > 0 && (
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">{t('plugin_detail.features')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {plugin.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-1.5 h-1.5 bg-purple-400 rounded-full mt-2.5 flex-shrink-0"></div>
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Examples Section - Audio Demos + Walkthrough + Gallery Image */}
        {(plugin.audioSamples && plugin.audioSamples.length > 0) || (plugin.gallery && plugin.gallery.length > 1) ? (
          <div className="mb-12">
            <div className="mb-6">
              <div className="border-b border-gray-700">
                <h2 className="text-purple-400 font-medium text-sm pb-4 border-b-2 border-purple-500 inline-block">
                  {t('plugin_detail.examples')}
                </h2>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              {/* Left: Audio Sections */}
              <div className={`space-y-8 ${plugin.gallery && plugin.gallery.length > 1 ? 'lg:w-1/2' : 'w-full'}`}>
                {/* Audio Demos Section */}
                {plugin.audioSamples && plugin.audioSamples.length > 0 && (
                  <div>
                    <AudioPlayer
                      samples={plugin.audioSamples}
                      currentAudio={currentAudio}
                      playingTrack={playingTrack}
                      isPaused={isPaused}
                      pluginSlug={plugin.slug}
                      onPlay={playAudio}
                    />
                  </div>
                )}

                {/* Walkthrough Section */}
                {plugin.videoUrl && (
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-4">{plugin.name} {t('plugin_detail.walkthrough')}</h3>
                    <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-6 rounded-xl border border-gray-700/50">
                      <div className="aspect-video bg-gray-700/50 rounded-lg flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto mb-4 bg-purple-500/20 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <p className="text-gray-400 text-sm">{t('plugin_detail.video_walkthrough_coming_soon')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Right: Gallery Image */}
              {plugin.gallery && plugin.gallery.length > 1 ? (
                <div className="lg:w-1/2">
                  <img
                    src={plugin.gallery[1]}
                    alt={`${plugin.name} interface`}
                    className="w-full rounded-lg shadow-lg"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/new_logo.png';
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Gallery Section - Skip first two images */}
        {plugin.gallery && plugin.gallery.length > 2 && (
          <div className="mb-12">
            <div className="mb-6">
              <div className="border-b border-gray-700">
                <h2 className="text-purple-400 font-medium text-sm pb-4 border-b-2 border-purple-500 inline-block">
                  {t('plugin_detail.gallery')}
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {plugin.gallery.slice(2).map((image, index) => (
                <img
                  key={index + 2}
                  src={image}
                  alt={`${plugin.name} screenshot ${index + 3}`}
                  className="w-full rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                />
              ))}
            </div>
          </div>
        )}

        {/* Specifications Section */}
        {plugin.specifications && (
          <div className="mb-12">
            <div className="mb-6">
              <div className="border-b border-gray-700">
                <h2 className="text-purple-400 font-medium text-sm pb-4 border-b-2 border-purple-500 inline-block">
                  {t('plugin_detail.specifications')}
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-sm text-gray-400 mb-1">{t('plugin_detail.format')}</div>
                <p className="text-gray-300">{plugin.specifications.format.join(', ')}</p>
              </div>
              <div>
                <div className="text-sm text-gray-400 mb-1">{t('plugin_detail.bit_depth')}</div>
                <p className="text-gray-300">{t(plugin.specifications.bitDepth)}</p>
              </div>
              <div>
                <div className="text-sm text-gray-400 mb-1">{t('plugin_detail.sample_rate')}</div>
                <p className="text-gray-300">{t(plugin.specifications.sampleRate)}</p>
              </div>
              {plugin.specifications.latency && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">{t('plugin_detail.latency')}</div>
                  <p className="text-gray-300">{t(plugin.specifications.latency)}</p>
                </div>
              )}
              <div>
                <div className="text-sm text-gray-400 mb-1">{t('plugin_detail.interface')}</div>
                <p className="text-gray-300">{t(plugin.specifications.interface)}</p>
              </div>
            </div>
          </div>
        )}

        {!owned && (
          <div className="text-center bg-gray-800/50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-white mb-4">
              {plugin.price === 0 ? 'Ready to Download?' : t('plugin_detail.ready_to_buy')}
            </h3>
            <p className="text-gray-300 mb-6">
              {plugin.price === 0 ? 'Get instant free access to this plugin' : t('plugin_detail.instant_download_message')}
            </p>
            <button
              type="button"
              onClick={handleBuyNow}
              className={`px-8 py-3 rounded-lg text-lg font-semibold transition-all duration-300 ${buyClass}`}
            >
              {plugin.price === 0 ? t('plugin_detail.free_download') : `${t('plugin_detail.buy_now_for')} $${plugin.price}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PluginDetail; 